import type { Action, InputManager } from '../core/input';

/**
 * On-screen buttons for touch devices. Uses pointer events with capture so a finger can
 * slide off a button without leaving it stuck down, and multiple fingers work at once.
 */
export class TouchControls {
  private buttons: HTMLButtonElement[];

  constructor(
    private root: HTMLElement,
    private input: InputManager,
    private onPause: () => void,
  ) {
    this.buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('.tbtn'));
    for (const b of this.buttons) {
      const key = b.dataset.key as Action | 'pause' | undefined;
      if (!key) continue;
      const set = (on: boolean) => {
        b.classList.toggle('pressed', on);
        if (key === 'pause') {
          if (on) this.onPause();
          return;
        }
        this.input.setTouch(key, on);
        // The jump button also drives the "up" action so pipes stay reachable.
        if (key === 'jump') this.input.setTouch('up', on);
      };
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        b.setPointerCapture(e.pointerId);
        set(true);
      });
      b.addEventListener('pointerup', (e) => {
        e.preventDefault();
        set(false);
      });
      b.addEventListener('pointercancel', () => set(false));
      b.addEventListener('lostpointercapture', () => set(false));
      b.addEventListener('contextmenu', (e) => e.preventDefault());
    }
  }

  setVisible(v: boolean): void {
    this.root.classList.toggle('hidden', !v);
    if (!v) {
      this.input.clearTouch();
      for (const b of this.buttons) b.classList.remove('pressed');
    }
  }

  static isTouchDevice(): boolean {
    return (
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0)
    );
  }
}
